import type { TopupQueueRow } from "@/modules/wallet/domain/types";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { CompactNumber } from "@/shared/ui/CompactNumber";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { DataTable, createSortableColumn, type TanstackReactTable } from "@/shared/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  TOPUP_STATUS_BADGE,
  TOPUP_STATUS_LABELS,
} from "./topup-queue-labels";
import {
  formatTopupMethodLabel,
  isPosQrCounterTopup,
  topupNeedsAdminApproval,
} from "./topup-queue-utils";

import { CheckIcon, ExternalLinkIcon, EyeIcon, XIcon } from "lucide-react";

type TopupQueueTableProps = {
  data: TopupQueueRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onViewDetail: (row: TopupQueueRow) => void;
  isApproving?: string | null;
  isRejecting?: string | null;
};

export function TopupQueueTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onSortingChange,
  onPaginationChange,
  onApprove,
  onReject,
  onViewDetail,
  isApproving,
  isRejecting,
}: TopupQueueTableProps) {
  const columns: TanstackReactTable.ColumnDef<TopupQueueRow>[] = [
    {
      accessorKey: "id",
      header: "ລຳດັບ",
      cell: ({ row }) => (
        <span className="font-mono text-muted-foreground text-xs">
          {row.original.id.slice(0, 8)}…
        </span>
      ),
      size: 100,
      enableSorting: false,
    },
    {
      accessorKey: "customerName",
      header: "ລູກຄ້າ",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {row.original.customerName ?? "–"}
          </span>
          <span className="text-muted-foreground text-xs">
            {row.original.customerPhone}
          </span>
        </div>
      ),
      size: 180,
      enableSorting: false,
    },
    {
      accessorKey: "method",
      header: "ວິທີ",
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <span className="text-sm">
            {formatTopupMethodLabel(row.original)}
          </span>
          {isPosQrCounterTopup(row.original) ? (
            <Badge variant="outline" className="w-fit text-xs">
              POS QR
            </Badge>
          ) : null}
        </div>
      ),
      size: 150,
      enableSorting: false,
    },
    {
      accessorKey: "amountLak",
      header: "ຍອດ (LAK)",
      cell: ({ row }) => (
        <CompactNumber
          value={Number(row.original.amountLak)}
          formatOptions={{
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }}
        />
      ),
      size: 110,
      enableSorting: false,
    },
    createSortableColumn<TopupQueueRow>("requestedAt", "ວັນທີຮ້ອງຂໍ", {
      size: 160,
      cell: ({ row }) => formatDateTimeLocal(row.original.requestedAt),
    }),
    {
      accessorKey: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge variant={TOPUP_STATUS_BADGE[row.original.status]}>
          {TOPUP_STATUS_LABELS[row.original.status]}
        </Badge>
      ),
      size: 120,
      enableSorting: false,
    },
    {
      accessorKey: "reviewedAt",
      header: "ດຳເນີນການເມື່ອ",
      cell: ({ row }) =>
        row.original.reviewedAt ? (
          formatDateTimeLocal(row.original.reviewedAt)
        ) : (
          <span className="text-muted-foreground text-sm">–</span>
        ),
      size: 160,
      enableSorting: false,
    },
    {
      accessorKey: "slipUrl",
      header: "ຫຼັກຖານ",
      cell: ({ row }) =>
        row.original.slipUrl ? (
          <a
            href={resolveImageSrc(row.original.slipUrl) ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary text-sm hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLinkIcon className="size-4" />
            ເບິ່ງຮູບ
          </a>
        ) : (
          <span className="text-muted-foreground text-sm">–</span>
        ),
      size: 100,
      enableSorting: false,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const item = row.original;
        const id = item.id;
        const approving = isApproving === id;
        const rejecting = isRejecting === id;

        if (topupNeedsAdminApproval(item)) {
          return (
            <div className="flex flex-wrap items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewDetail(item)}
              >
                <EyeIcon className="size-4" />
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => onApprove(id)}
                disabled={approving || rejecting}
              >
                {approving ? "…" : <CheckIcon className="size-4" />}
                ອະນຸມັດ
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onReject(id)}
                disabled={approving || rejecting}
              >
                {rejecting ? "…" : <XIcon className="size-4" />}
                ປະຕິເສດ
              </Button>
            </div>
          );
        }

        if (isPosQrCounterTopup(item) && item.status === "APPROVED") {
          return (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-muted-foreground text-xs">
                ຢືນຢັນທີ່ POS ແລ້ວ
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewDetail(item)}
              >
                <EyeIcon className="size-4" />
                ລາຍລະອຽດ
              </Button>
            </div>
          );
        }

        return (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-xs">ດຳເນິນການແລ້ວ</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewDetail(item)}
            >
              <EyeIcon className="size-4" />
            </Button>
          </div>
        );
      },
      size: 220,
      enableSorting: false,
    },
  ];

  return (
    <DataTable<TopupQueueRow, unknown>
      noDataMessage="ບໍ່ພົບລາຍການເຕີມເງິນ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
